import { useState } from "react";
import { Calculator as CalcIcon, Loader2 } from "lucide-react";
import { calculatePhrase, fetchAnagrams } from "@/lib/api";
import { toast } from "sonner";
import CipherTable from "@/components/calc/CipherTable";
import SaveToDictionary from "@/components/calc/SaveToDictionary";
import AnagramPanel from "@/components/calc/AnagramPanel";

export default function Calculator() {
  const [text, setText] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anagrams, setAnagrams] = useState(null);
  const [anagramsLoading, setAnagramsLoading] = useState(false);

  const compute = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setAnagramsLoading(true);
    setAnagrams(null);
    try {
      const [r, a] = await Promise.all([
        calculatePhrase(text),
        fetchAnagrams(text).catch(() => null),
      ]);
      setData(r);
      setAnagrams(a);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
      setAnagramsLoading(false);
    }
  };

  return (
    <section className="space-y-10">
      <div className="rise">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-3">
          // Gematria Calculator
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
          Compute the <span className="gold italic">full fingerprint</span>
        </h1>
        <p className="muted mt-4 max-w-2xl leading-relaxed">
          Enter any name, phrase or date as text. Every cipher computed at once.
        </p>
      </div>

      <div className="rise rise-1 panel p-6 md:p-8">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">
          Phrase / Name
        </label>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="phrase-calculator-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && compute()}
            placeholder='e.g. "Lord Jacob Rothschild"'
            className="terminal-input flex-1 w-full min-w-0 px-4 py-4 text-xl"
          />
          <button
            data-testid="phrase-calculator-submit"
            onClick={compute}
            disabled={loading}
            className="btn-primary px-6 py-4 text-sm flex items-center justify-center gap-2 sm:shrink-0"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <CalcIcon size={16} />}
            Compute
          </button>
        </div>
      </div>

      {data && (
        <div className="rise rise-2 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <CipherTable data={data} />
          <SaveToDictionary text={text} />
        </div>
      )}

      {/* key={data.text} resets the panel's "show all" state on each new phrase */}
      {data && (
        <AnagramPanel key={data.text} text={text} anagrams={anagrams} loading={anagramsLoading} />
      )}
    </section>
  );
}
